import type { AuthSuccess } from "@reelio/contracts";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Navigate, useLocation } from "react-router-dom";

import {
  ApiRequestError,
  getCurrentUser,
  logout as requestLogout,
} from "./api";

type AuthStatus = "loading" | "authenticated" | "anonymous";

type AuthContextValue = {
  status: AuthStatus;
  viewer: AuthSuccess | null;
  setAuthenticated: (viewer: AuthSuccess) => void;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [viewer, setViewer] = useState<AuthSuccess | null>(null);

  const refresh = useCallback(async () => {
    try {
      const nextViewer = await getCurrentUser();
      setViewer(nextViewer);
      setStatus("authenticated");
    } catch (error) {
      if (
        error instanceof ApiRequestError &&
        ["not_authenticated", "session_expired"].includes(error.code)
      ) {
        setViewer(null);
        setStatus("anonymous");
        return;
      }
      setViewer(null);
      setStatus("anonymous");
      throw error;
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    void getCurrentUser(controller.signal)
      .then((nextViewer) => {
        setViewer(nextViewer);
        setStatus("authenticated");
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setViewer(null);
        setStatus("anonymous");
      });

    return () => controller.abort();
  }, []);

  const setAuthenticated = useCallback((nextViewer: AuthSuccess) => {
    setViewer(nextViewer);
    setStatus("authenticated");
  }, []);

  const signOut = useCallback(async () => {
    try {
      await requestLogout();
    } finally {
      setViewer(null);
      setStatus("anonymous");
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      viewer,
      setAuthenticated,
      refresh,
      signOut,
    }),
    [refresh, setAuthenticated, signOut, status, viewer],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const value = useContext(AuthContext);

  if (!value) {
    throw new Error("useAuth must be used within AuthProvider.");
  }

  return value;
};

export const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return <div className="route-loading">Opening your workspace…</div>;
  }

  if (status === "anonymous") {
    const redirect = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/auth/login?redirect=${encodeURIComponent(redirect)}`}
        replace
      />
    );
  }

  return children;
};
